import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getUtmParams, sendFormEvent } from '@/lib/utm';

interface RequestFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const RequestFormDialog = ({ open, onOpenChange }: RequestFormDialogProps) => {
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    telegram: '',
    project: '',
  });
  const [sending, setSending] = useState(false);

  const handleTelegramChange = (value: string) => {
    if (value && !value.startsWith('@')) {
      value = '@' + value.replace(/^@+/, '');
    }
    setFormData({ ...formData, telegram: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-telegram', {
        body: { ...formData, source: 'popup', utm: getUtmParams() },
      });
      if (error) throw error;
      sendFormEvent('popup');
      toast.success('Заявка отправлена!');
      onOpenChange(false);
      setFormData({ name: '', contact: '', telegram: '', project: '' });
    } catch (err: any) {
      console.error(err);
      toast.error('Ошибка отправки. Попробуйте позже.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-card border-border p-0 overflow-hidden">
        <div className="relative p-8 md:p-10">
          {/* Close button */}
          <button 
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={16} />
          </button>

          <DialogHeader className="mb-10 text-left">
            <DialogTitle className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight">
              <span className="text-gradient-red">Contact us</span>
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-muted-foreground text-base mb-3">Your name</label>
              <Input
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
                required
              />
            </div>

            <div>
              <label className="block text-muted-foreground text-base mb-3">Phone or E-mail</label>
              <Input
                placeholder="+1 234 567 8900 or email@example.com"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
              />
            </div>

            <div>
              <label className="block text-muted-foreground text-base mb-3">Your Telegram</label>
              <Input
                placeholder="@telegram"
                value={formData.telegram}
                onChange={(e) => handleTelegramChange(e.target.value)}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
              />
            </div>

            <div>
              <label className="block text-muted-foreground text-base mb-3">Project name</label>
              <Input
                placeholder="Your project name or URL"
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                className="bg-muted/50 border-0 rounded-lg h-14 text-base px-5 placeholder:text-muted-foreground/50"
                required
              />
            </div>

            <Button type="submit" variant="outline" className="rounded-full h-14 px-10 text-base mt-4" disabled={sending}>
              {sending ? <><Loader2 size={16} className="mr-2 animate-spin" /> Отправка...</> : 'Leave a request'}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RequestFormDialog;
